/**
 * Mirrors backend adult/minor rules for client-side form validation.
 * Adult when the 18th birthday is today or earlier; minor otherwise.
 * Returns null when dateOfBirth is missing/invalid.
 */
export function isStudentAdult(dateOfBirth: string | null | undefined): boolean | null {
  if (!dateOfBirth?.trim()) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();

  // 18th birthday = DOB + 18 years; adult if that day is not after today.
  const adultY = year + 18;
  if (adultY !== todayY) return adultY < todayY;
  if (month !== todayM) return month < todayM;
  return day <= todayD;
}

export function isStudentMinor(dateOfBirth: string | null | undefined): boolean {
  return isStudentAdult(dateOfBirth) === false;
}

export function isDateOfBirthInFuture(dateOfBirth: string | null | undefined): boolean {
  if (!dateOfBirth?.trim()) return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth.trim());
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();
  if (year !== todayY) return year > todayY;
  if (month !== todayM) return month > todayM;
  return day > todayD;
}

export type StudentProfileFormValues = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  guardianContact: string;
  notes: string;
};

export type StudentProfileFormErrors = Partial<Record<keyof StudentProfileFormValues, string>>;

/** Client-side checks aligned with backend validateStudentProfileRequest. */
export function validateStudentProfileForm(
  values: StudentProfileFormValues,
  messages: {
    dateOfBirthFuture: string;
    guardianContactRequired: string;
  }
): StudentProfileFormErrors {
  const errors: StudentProfileFormErrors = {};
  const dob = values.dateOfBirth.trim() || null;
  if (isDateOfBirthInFuture(dob)) {
    errors.dateOfBirth = messages.dateOfBirthFuture;
  }
  if (isStudentMinor(dob) && !values.guardianContact.trim()) {
    errors.guardianContact = messages.guardianContactRequired;
  }
  return errors;
}

export function toUpdateStudentProfileRequest(values: StudentProfileFormValues) {
  return {
    firstName: values.firstName.trim() || null,
    lastName: values.lastName.trim() || null,
    dateOfBirth: values.dateOfBirth.trim() || null,
    guardianContact: values.guardianContact.trim() || null,
    notes: values.notes.trim() || null,
  };
}
